import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import toast from 'react-hot-toast';

import { PunchModal, type PunchMode } from '@/components/PunchModal';
import { attendanceService } from '@/services/attendance.service';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/utils/cn';

function formatElapsed(fromIso: string): string {
  const start = new Date(fromIso).getTime();
  const ms = Date.now() - start;
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function AttendancePill() {
  const role = useAuthStore((s) => s.user?.role);
  const [, force] = useState(0);
  const [confirm, setConfirm] = useState<PunchMode | null>(null);

  const queryClient = useQueryClient();
  const todayQuery = useQuery({
    queryKey: ['attendance', 'me', 'today'],
    queryFn: () => attendanceService.getToday(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    enabled: role !== 'OWNER',
  });

  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (role === 'OWNER') return null;

  const record = todayQuery.data?.record ?? null;

  const state: {
    mode: PunchMode | 'done';
    label: string;
    elapsed?: string;
    sub?: string;
  } = useMemo(() => {
    if (!record || !record.checkIn) return { mode: 'in', label: 'Check in' };
    if (!record.checkOut) {
      return {
        mode: 'out',
        label: 'Check out',
        elapsed: formatElapsed(record.checkIn.at),
        sub: `In at ${record.checkIn.time}`,
      };
    }
    return {
      mode: 'done',
      label: `Done · ${record.checkOut.time}`,
      sub: `${record.checkIn.time} – ${record.checkOut.time}`,
    };
  }, [record]);

  const onClick = () => {
    if (state.mode === 'done') {
      toast.success("You're all set for today.");
      return;
    }
    setConfirm(state.mode);
  };

  const baseClasses =
    'group relative inline-flex items-center rounded-xl text-xs font-semibold transition-all';
  const variantClasses =
    state.mode === 'in'
      ? 'bg-gradient-to-r from-rose-500 to-orange-500 px-3.5 py-2 text-white shadow-lg shadow-rose-500/30 ring-1 ring-rose-300/60 hover:shadow-rose-500/50 hover:scale-[1.02]'
      : state.mode === 'out'
        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 py-2 text-white shadow-lg shadow-emerald-500/30 ring-1 ring-emerald-300/60 hover:shadow-emerald-500/50 hover:scale-[1.02]'
        : 'bg-secondary px-3 py-1.5 text-secondary-foreground';

  return (
    <>
      <button onClick={onClick} className={cn(baseClasses, variantClasses)}>
        {state.mode === 'in' && (
          <span
            aria-hidden
            className="pointer-events-none absolute -inset-0.5 rounded-xl bg-rose-400/40 opacity-75 blur-sm animate-pulse"
          />
        )}

        {state.mode === 'in' && (
          <span className="relative inline-flex items-center gap-2">
            <span className="relative inline-flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white ring-2 ring-rose-200" />
            </span>
            <span className="leading-tight">{state.label}</span>
          </span>
        )}

        {state.mode === 'out' && (
          <span className="relative inline-flex items-center">
            <span className="inline-flex items-center gap-1.5 pl-3 pr-3">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
              </span>
              <span className="font-mono text-[11px] tabular-nums tracking-tight">
                {state.elapsed}
              </span>
            </span>
            <span className="h-5 w-px bg-white/40" />
            <span className="inline-flex items-center gap-1 pl-3 pr-3.5">
              <LogOut className="h-3.5 w-3.5" />
              {state.label}
            </span>
          </span>
        )}

        {state.mode === 'done' && (
          <span className="relative inline-flex items-center gap-2">
            <LogOut className="h-3.5 w-3.5" />
            <span className="leading-tight">{state.label}</span>
          </span>
        )}
      </button>

      <PunchModal
        open={!!confirm}
        mode={confirm ?? 'in'}
        onClose={() => setConfirm(null)}
        onDone={() => {
          setConfirm(null);
          queryClient.invalidateQueries({ queryKey: ['attendance'] });
        }}
      />
    </>
  );
}
